import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import { AuthShell } from "@/components/AuthShell";
import { PageLoader } from "@/components/BrandedSpinner";
import { Button } from "@/components/ui/button";

import { userManager } from "./oidc-config";

/**
 * Lands here after Authentik redirects back from the OIDC code flow.
 *
 * AuthProvider is configured with `skipSigninCallback: true`, so we own
 * the code/state exchange. We call `userManager.signinCallback()` exactly
 * once per code/state pair, then forward to /dashboard.
 *
 * Why we own it instead of AuthProvider:
 *   React 19 StrictMode double-invokes effects in development, and Vite
 *   HMR can re-mount mid-flow. AuthProvider's internal effect calls
 *   `userManager.signinCallback()` on every fire; the first call removes
 *   the state from storage, the second throws "No matching state found in
 *   storage" and the user sees a sign-in failure that "fixes itself" on
 *   reload (because reload lands without code/state and the SSO cookie at
 *   Authentik silently re-issues a session).
 *
 * Dedupe mechanism:
 *   1. `inFlight` is a module-scoped Promise that survives mount/unmount/
 *      remount. Both StrictMode invocations of the effect see the same
 *      Promise and await the same result — only one network exchange runs.
 *   2. `processedHref` snapshots the href that was processed. If the same
 *      effect fires after the URL has already been cleaned (a remount
 *      between `signinCallback` resolving and our `navigate` call), we
 *      short-circuit immediately.
 *
 * Refs:
 *   https://github.com/authts/react-oidc-context/issues/1501
 *   https://github.com/authts/react-oidc-context/issues/1587
 *   https://github.com/authts/oidc-client-ts/issues/1487
 */

let inFlight: Promise<void> | null = null;
let processedHref: string | null = null;

function hasAuthParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("code") && params.has("state");
}

export function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Once the AuthProvider has propagated the user-loaded event into
    // `auth.isAuthenticated`, leave /auth/callback. Crucially, this is
    // the ONLY place we navigate away — kicking off the navigate
    // earlier (e.g. immediately after `signinCallback()` resolves) is
    // a footgun: React hasn't yet re-rendered with the new auth state,
    // so DashboardRedirector mounts seeing isAuthenticated=false,
    // fires another signinRedirect(), and we loop through Authentik's
    // SSO cookie until something blows up.
    if (auth.isAuthenticated) {
      void navigate({ to: "/dashboard", replace: true });
      return;
    }

    // Already processed this exact href, but auth state hasn't
    // propagated yet — wait for the next render (this effect re-runs
    // when isAuthenticated changes).
    if (processedHref === window.location.href) return;

    // No code/state on the URL — nothing to exchange. The user landed
    // here without auth params (or after URL was cleaned). The early
    // return above handles the authenticated case.
    if (!hasAuthParams()) return;

    // Coalesce concurrent invocations (StrictMode double-fire) onto one promise.
    if (!inFlight) {
      inFlight = userManager
        .signinCallback(window.location.href)
        .then(() => {
          processedHref = window.location.href;
          // Strip code/state from the URL so a refresh on
          // /auth/callback doesn't try to re-exchange a spent code.
          window.history.replaceState({}, document.title, window.location.pathname);
          // Don't navigate from here — wait for the AuthProvider to
          // pick up the userLoaded event from userManager and flip
          // auth.isAuthenticated. The early return above handles it.
        })
        .catch((err: unknown) => {
          inFlight = null;
          throw err instanceof Error ? err : new Error(String(err));
        });
    }

    // Surface failures only — successful navigation is driven by the
    // auth.isAuthenticated flip on the next render.
    inFlight.catch((err: Error) => {
      // CHP-809: keep the raw oidc-client-ts string (e.g. "No matching
      // state found in storage", "invalid_grant") out of the user-facing
      // copy — it's an internal detail. Log it for DevTools/Sentry, show
      // a friendly generic line instead.
      console.error("OIDC callback failed:", err);
      setError(err);
    });
  }, [auth.isAuthenticated, navigate]);

  if (error) {
    return (
      <AuthShell
        headingId="auth-callback-error"
        heading="Sign-in failed"
        headingClassName="text-2xl font-semibold tracking-tight text-destructive"
        bodyClassName="space-y-4"
      >
        <div role="alert" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            We couldn't complete your sign-in. Please try again.
          </p>
          <Button
            type="button"
            className="w-full"
            onClick={() => {
              processedHref = null;
              // ADR-0010: route back to PRM-rendered /login.
              void navigate({ to: "/login", replace: true });
            }}
          >
            Try again
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-card text-card-foreground">
      <PageLoader label="Completing sign-in…" />
    </div>
  );
}
